import { Injectable, inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { MonerisCheckoutLoaderService } from './moneris-checkout-loader.service';
import type { MonerisCheckoutInstance } from './moneris.types';

/** Callbacks the Review payment flow registers with Moneris Checkout. */
export interface MonerisCheckoutCallbacks {
  onPageLoaded?: (data: string) => void;
  onCancel?: (data: string) => void;
  onPaymentComplete: (ticketJson: string) => void;
  onError?: (data: string) => void;
}

/** Max height for the Moneris-hosted iframe (overrides a too-short MRC default). */
export const MONERIS_CHECKOUT_IFRAME_MAX_HEIGHT_PX = 900;

/** Viewport chrome reserved for modal header, note, footer, and padding. */
const MONERIS_CHECKOUT_MODAL_CHROME_PX = 200;

/** Absolute floor on very short viewports. */
const MONERIS_CHECKOUT_IFRAME_MIN_HEIGHT_PX = 360;

/**
 * Thin wrapper around the global Moneris Checkout SDK for hosted card capture.
 */
@Injectable({ providedIn: 'root' })
export class MonerisCheckoutService {
  private readonly loader = inject(MonerisCheckoutLoaderService);
  private checkout: MonerisCheckoutInstance | null = null;
  private iframeObserver: MutationObserver | null = null;

  /**
   * Loads the SDK (if needed), configures callbacks, and starts hosted checkout for a ticket.
   */
  async startCheckout(
    ticket: string,
    hostDivId: string,
    callbacks: MonerisCheckoutCallbacks,
  ): Promise<void> {
    await this.loader.loadScript();
    const Ctor = window.monerisCheckout;
    if (!Ctor) {
      throw new Error('Moneris Checkout constructor is not available.');
    }

    this.disconnectIframeObserver();

    const checkout = new Ctor();
    this.checkout = checkout;
    checkout.setMode(environment.monerisMode);
    checkout.setCheckoutDiv(hostDivId);
    checkout.setCallback('page_loaded', (data: string) => {
      this.resizeCheckoutIframe(hostDivId);
      callbacks.onPageLoaded?.(data);
    });
    if (callbacks.onCancel) {
      checkout.setCallback('cancel_transaction', callbacks.onCancel);
    }
    checkout.setCallback('payment_complete', callbacks.onPaymentComplete);
    if (callbacks.onError) {
      checkout.setCallback('error_event', callbacks.onError);
    }
    checkout.startCheckout(ticket);
    this.watchAndResizeCheckoutIframe(hostDivId);
  }

  /**
   * Asks Moneris to close the hosted checkout session when still open.
   */
  closeCheckout(ticket?: string): void {
    this.disconnectIframeObserver();
    this.checkout?.closeCheckout(ticket);
    this.checkout = null;
  }

  /**
   * Moneris injects an iframe with a short fixed height from MRC config.
   * Size it to the modal content area so only the iframe scrolls (no outer modal scroll).
   */
  private resizeCheckoutIframe(hostDivId: string): boolean {
    const host = document.getElementById(hostDivId);
    const iframe = host?.querySelector('iframe');
    if (!host || !iframe) {
      return false;
    }

    const heightPx = this.resolveCheckoutIframeHeightPx();
    const height = `${heightPx}px`;
    host.style.height = height;
    iframe.style.setProperty('height', height, 'important');
    iframe.style.setProperty('min-height', height, 'important');
    iframe.style.setProperty('width', '100%', 'important');
    iframe.setAttribute('height', String(heightPx));
    return true;
  }

  /** Fits the iframe inside ~95vh minus modal chrome, capped for large screens. */
  private resolveCheckoutIframeHeightPx(): number {
    const viewportBudget = Math.floor(window.innerHeight * 0.95) - MONERIS_CHECKOUT_MODAL_CHROME_PX;
    return Math.max(
      MONERIS_CHECKOUT_IFRAME_MIN_HEIGHT_PX,
      Math.min(MONERIS_CHECKOUT_IFRAME_MAX_HEIGHT_PX, viewportBudget),
    );
  }

  /**
   * Watches the mount div until Moneris inserts the iframe, then resizes it.
   */
  private watchAndResizeCheckoutIframe(hostDivId: string): void {
    if (this.resizeCheckoutIframe(hostDivId)) {
      return;
    }

    const host = document.getElementById(hostDivId);
    if (!host || typeof MutationObserver === 'undefined') {
      return;
    }

    this.iframeObserver = new MutationObserver(() => {
      if (this.resizeCheckoutIframe(hostDivId)) {
        this.disconnectIframeObserver();
      }
    });
    this.iframeObserver.observe(host, { childList: true, subtree: true });
  }

  private disconnectIframeObserver(): void {
    this.iframeObserver?.disconnect();
    this.iframeObserver = null;
  }
}
